import { useState } from 'react';
import toast from 'react-hot-toast';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { useVmActions } from '@/hooks/useDispatcher';
import type { Vm } from '@/types/api';

interface Props {
  open: boolean;
  onClose: () => void;
  vm?: Vm;
}

export function VmFormDialog({ open, onClose, vm }: Props) {
  const { create, update, testConnection } = useVmActions();
  const [slug, setSlug] = useState(vm?.slug ?? '');
  const [baseUrl, setBaseUrl] = useState(vm?.base_url ?? '');
  const [token, setToken] = useState('');
  const [region, setRegion] = useState(vm?.region ?? 'fr');
  const [weight, setWeight] = useState(vm?.weight ?? 1);
  const [maxConcurrent, setMaxConcurrent] = useState(vm?.max_concurrent ?? 4);
  const editing = !!vm;

  async function handleTest() {
    if (!baseUrl || !token) {
      toast.error('URL et token requis pour tester');
      return;
    }
    try {
      const res = await testConnection.mutateAsync({ base_url: baseUrl, token });
      if (res.ok) toast.success('Connexion OK');
      else toast.error(res.details ?? 'Connexion KO');
    } catch {
      toast.error('Test impossible');
    }
  }

  async function handleSubmit() {
    try {
      if (editing && vm) {
        await update.mutateAsync({
          id: vm.id,
          body: { slug, base_url: baseUrl, region, weight, max_concurrent: maxConcurrent },
        });
        toast.success('VM mise à jour');
      } else {
        await create.mutateAsync({
          slug,
          base_url: baseUrl,
          token,
          region,
          weight,
          max_concurrent: maxConcurrent,
        });
        toast.success('VM ajoutée');
      }
      onClose();
    } catch {
      toast.error('Échec de l\'enregistrement');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? `Modifier ${vm?.slug}` : 'Ajouter une VM LeadBridge'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="secondary" onClick={handleTest} loading={testConnection.isPending}>
            Tester
          </Button>
          <Button onClick={handleSubmit} loading={create.isPending || update.isPending}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Slug</Label>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="vm-fr-1" />
        </div>
        <div className="col-span-2">
          <Label>Base URL</Label>
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://lb-1.example.com"
          />
        </div>
        {!editing && (
          <div className="col-span-2">
            <Label>Token (write-only)</Label>
            <Input
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="leadbridge token"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Stocké chiffré (AES-GCM). Jamais affiché ensuite.
            </p>
          </div>
        )}
        <div>
          <Label>Région</Label>
          <Input value={region} onChange={(e) => setRegion(e.target.value)} />
        </div>
        <div>
          <Label>Poids</Label>
          <Input
            type="number"
            min={1}
            value={weight}
            onChange={(e) => setWeight(Number(e.target.value))}
          />
        </div>
        <div className="col-span-2">
          <Label>Concurrence max</Label>
          <Input
            type="number"
            min={1}
            value={maxConcurrent}
            onChange={(e) => setMaxConcurrent(Number(e.target.value))}
          />
        </div>
      </div>
    </Dialog>
  );
}
